import { useEffect, useState } from "react";
import { Card, Dialog, Grid, DialogTitle, IconButton, DialogContent } from "@mui/material";
import SuiBox from "components/SuiBox";
import SuiTypography from "components/SuiTypography";
import SuiButton from "components/SuiButton";
import DashboardLayout from "custom/LayoutContainers/DashboardLayout";
import DashboardNavbar from "custom/Navbars/DashboardNavbar";
import { connect } from "react-redux";
import Alert from "custom/Alert";
import PropTypes from "prop-types";
import Swal from "sweetalert2";
import MessageTypes from "enums/message-types";
import { removeMessage } from "redux/actions/message";
import { useHistory } from "react-router-dom";
import { FileXls, X } from "phosphor-react";
import { USER_IMPORT, userImport } from "redux/actions/pages/user";

import ExcelJS from "exceljs";

const UserImport = ({ importUsersAction, localizations, message, removeErrorMsg }) => {
  const [selectedFile, setSelectedFile] = useState(null);
  const [fileName, setFileName] = useState("");
  const history = useHistory();

  const [previewData, setPreviewData] = useState(null);
  const [headers, setHeaders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showPreview, setShowPreview] = useState(false);

  const [currentPage, setCurrentPage] = useState(1);
  const rowsPerPage = 10;

  useEffect(() => {
    if (message) {
      Swal.fire({
        icon: message.type || "error",
        title:
          message.status === MessageTypes.success
            ? localizations?.MESSAGE_SUCCESS
            : localizations?.MESSAGE_ERROR,
        text: message.message.message,
        showCancelButton: false,
        confirmButtonText: localizations?.MESSAGE_GO_HOME_CONFIRM_BTN ?? "Tamam",
      }).then(() => {
        removeErrorMsg(USER_IMPORT);
        history.push("/user/import");
      });
    }
  }, [message]);

  const handleFileChange = async (event) => {
    const file = event.target.files[0];
    if (file) {
      const validTypes = [
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        "application/vnd.ms-excel",
      ];

      if (!validTypes.includes(file.type)) {
        Alert.error("Lütfen geçerli bir Excel dosyası seçiniz.");
        return;
      }

      setSelectedFile(file);
      setFileName(file.name);
      setLoading(true);

      try {
        const arrayBuffer = await file.arrayBuffer();
        const workbook = new ExcelJS.Workbook();
        await workbook.xlsx.load(arrayBuffer);
        const worksheet = workbook.worksheets[0];
        const rows = [];
        worksheet.eachRow((row, rowNumber) => {
          const rowData = [];
          row.eachCell({ includeEmpty: true }, (cell) => {
            let cellValue = cell.value;

            if (cellValue && typeof cellValue === "object") {
              if (cellValue.text && cellValue.hyperlink) {
                cellValue = cellValue.text;
              } else if (cellValue.richText) {
                cellValue = cellValue.richText.map((t) => t.text).join("");
              } else if (cellValue.formula) {
                cellValue = cellValue.result || cellValue.formula;
              } else if (cellValue instanceof Date) {
                cellValue = cellValue.toLocaleDateString("tr-TR");
              } else {
                cellValue = JSON.stringify(cellValue);
              }
            }

            rowData.push(cellValue);
          });

          if (rowNumber === 1) {
            setHeaders(rowData);
          } else {
            rows.push(rowData);
          }
        });
        setPreviewData(rows);
      } catch (error) {
        Alert.error("Dosya okunurken bir hata oluştu.");
        // eslint-disable-next-line
        console.error(error);
      } finally {
        setLoading(false);
      }
    }
  };

  const handleUpload = () => {
    if (selectedFile) {
      const formData = new FormData();
      formData.append("file", selectedFile);

      importUsersAction(formData);

      setSelectedFile(null);
      setFileName("");
      setPreviewData(null);
    }
  };

  const indexOfLastRow = currentPage * rowsPerPage;
  const indexOfFirstRow = indexOfLastRow - rowsPerPage;
  const currentRows = previewData ? previewData.slice(indexOfFirstRow, indexOfLastRow) : [];
  const totalPages = previewData ? Math.ceil(previewData.length / rowsPerPage) : 0;

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <SuiBox mt={3} mb={3}>
        <Grid container>
          <Grid item xs={12}>
            <Card>
              <SuiBox p={3} textAlign="center">
                <SuiTypography variant="h5" fontWeight="medium" mb={1}>
                  {localizations?.USER_IMPORT ?? "Toplu Kullanıcı Yükleme"}
                </SuiTypography>
                <SuiTypography variant="body2" color="text" mb={3}>
                  {localizations?.USER_IMPORT_TYPE ?? "Excel (.xlsx) dosyanızı seçerek yükleyin."}
                </SuiTypography>

                <SuiBox
                  mb={3}
                  p={2}
                  sx={{
                    backgroundColor: "#e3f2fd",
                    borderRadius: "0.75rem",
                    border: "1px dashed #2196f3",
                  }}
                >
                  <SuiBox display="flex" alignItems="center" justifyContent="center" gap={2}>
                    <SuiBox textAlign="left">
                      <SuiTypography variant="button" fontWeight="bold" color="info">
                        📥 Şablon Dosyayı İndirin
                      </SuiTypography>
                      <SuiTypography variant="caption" color="text" display="block" mt={0.5}>
                        Doğru formatta Excel hazırlamak için önce şablonu indirin
                      </SuiTypography>
                    </SuiBox>

                    <SuiButton
                      variant="contained"
                      color="info"
                      size="medium"
                      component="a"
                      href="/templates/user-import-template.xlsx"
                      download="Kullanici_Yukleme_Sablonu.xlsx"
                    >
                      İndir
                    </SuiButton>
                  </SuiBox>
                </SuiBox>

                <SuiBox
                  component="div"
                  sx={{
                    border: "2px dashed #d2d6da",
                    borderRadius: "0.75rem",
                    padding: "2rem",
                    cursor: "pointer",
                    backgroundColor: selectedFile ? "#f8f9fa" : "transparent",
                    "&:hover": { backgroundColor: "#f8f9fa" },
                  }}
                  onClick={() => document.getElementById("file-upload-input").click()}
                >
                  <input
                    type="file"
                    id="file-upload-input"
                    hidden
                    accept=".xlsx, .xls"
                    onChange={handleFileChange}
                  />

                  <FileXls size={100} weight="duotone" color="#7493b6" />

                  <SuiBox mt={2}>
                    <SuiTypography variant="button" fontWeight="bold">
                      {fileName || (localizations?.SELECT_FILE ?? "Dosya Seçmek İçin Tıklayın")}
                    </SuiTypography>
                  </SuiBox>
                </SuiBox>

                <SuiBox mt={4} display="flex" gap={2} justifyContent="flex-end">
                  <SuiButton
                    variant="contained"
                    color="dark"
                    size="large"
                    onClick={handleUpload}
                    disabled={!selectedFile}
                  >
                    {localizations?.START_IMPORT ?? "Yüklemeyi Başlat"}
                  </SuiButton>

                  <SuiButton
                    variant="outlined"
                    color="dark"
                    size="large"
                    onClick={() => {
                      setShowPreview(!showPreview);
                      setCurrentPage(1);
                    }}
                    disabled={!previewData || loading}
                  >
                    {localizations?.PREVIEW ?? "Önizleme"}
                  </SuiButton>
                </SuiBox>
              </SuiBox>
            </Card>
          </Grid>
        </Grid>
      </SuiBox>

      <Dialog open={showPreview} onClose={() => setShowPreview(false)} fullScreen>
        <DialogTitle>
          <SuiBox display="flex" justifyContent="space-between" alignItems="center">
            <SuiTypography variant="h5">Dosya Önizleme</SuiTypography>
            <SuiTypography variant="body2" color="text" mt={0.5}>
              Toplam {previewData?.length || 0} kayıt bulundu
            </SuiTypography>
            <IconButton onClick={() => setShowPreview(false)}>
              <X size={24} />
            </IconButton>
          </SuiBox>
        </DialogTitle>
        <DialogContent dividers>
          {showPreview && previewData && headers.length > 0 && (
            <SuiBox>
              <SuiBox sx={{ overflowX: "auto" }}>
                <table style={{ width: "100%", borderCollapse: "collapse" }}>
                  <thead>
                    <tr>
                      {headers.map((header, index) => (
                        <th
                          // eslint-disable-next-line
                          key={`header-${header}-${index}`}
                          style={{
                            border: "1px solid #ddd",
                            padding: "12px",
                            backgroundColor: "#f2f2f2",
                            textAlign: "left",
                            fontWeight: "bold",
                          }}
                        >
                          {header}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {currentRows.map((row, rowIndex) => (
                      <tr
                        // eslint-disable-next-line
                        key={`row-${rowIndex}`}
                      >
                        {row.map((cell, cellIndex) => (
                          <td
                            // eslint-disable-next-line
                            key={`cell-${rowIndex}-${cellIndex}`}
                            style={{
                              border: "1px solid #ddd",
                              padding: "12px",
                            }}
                          >
                            {cell !== null && cell !== undefined ? String(cell) : "-"}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </SuiBox>
              {previewData.length > rowsPerPage && (
                <SuiBox mt={3} display="flex" justifyContent="space-between" alignItems="center">
                  {previewData && previewData.length > rowsPerPage && (
                    <SuiBox
                      mt={3}
                      display="flex"
                      justifyContent="space-between"
                      alignItems="center"
                      gap={2}
                    >
                      <SuiTypography variant="body2" color="text">
                        Gösterilen: {indexOfFirstRow + 1} -{" "}
                        {Math.min(indexOfLastRow, previewData.length)} / {previewData.length}
                      </SuiTypography>

                      <SuiBox display="flex" gap={1} alignItems="center">
                        <SuiButton
                          size="small"
                          variant="outlined"
                          color="dark"
                          onClick={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
                          disabled={currentPage === 1}
                        >
                          ← Önceki
                        </SuiButton>

                        <SuiTypography variant="button" sx={{ mx: 2 }}>
                          {currentPage} / {totalPages}
                        </SuiTypography>

                        <SuiButton
                          size="small"
                          variant="outlined"
                          color="dark"
                          onClick={() => setCurrentPage((prev) => Math.min(totalPages, prev + 1))}
                          disabled={currentPage === totalPages}
                        >
                          Sonraki →
                        </SuiButton>
                      </SuiBox>
                    </SuiBox>
                  )}
                </SuiBox>
              )}
            </SuiBox>
          )}
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
};

UserImport.defaultProps = {
  importUsersAction: () => {},
  localizations: null,
  message: null,
};

UserImport.propTypes = {
  importUsersAction: PropTypes.func,
  localizations: PropTypes.objectOf(PropTypes.any),
  message: PropTypes.objectOf(PropTypes.any),
  removeErrorMsg: PropTypes.func.isRequired,
};

const mapStateToProps = (state) => ({
  localizations: state.localizations,
  message: state.notifications?.[0],
});

const mapDispatchToProps = {
  importUsersAction: userImport,
  removeErrorMsg: removeMessage,
};

export default connect(mapStateToProps, mapDispatchToProps)(UserImport);
